import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "@digital-twins/design-system/icons";
import {
  SOCIAL_OTHER_THEME_ID,
  SocialPlatform,
  SocialPostSource,
  SocialSentiment,
  SocialTopicCountsResponse,
  SocialTopicMetric,
} from "@digital-twins/shared-types";
import { intlLocaleForUi } from "@/i18n";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { SocialBrandMark } from "./SocialBrandMark";
import { SocialPlatformIcon } from "./SocialPlatformIcon";
import { SocialTopicSlotContext } from "./SocialTopicView";
import { measureOf } from "./charts/socialTopicMetrics";
import { formatSocialCount, SOCIAL_PLATFORM_LABELS, socialSourceFacet } from "./socialFormat";
import { useSocialUniverseCounts } from "./useSocialUniverse";
import { SocialTopicFacet, SocialTopicFilterState } from "./useSocialTopicFilters";

/** At this width and below a menu opens as a bottom sheet (concept §5.2). */
export const SOCIAL_FILTER_SHEET_QUERY = "(max-width: 760px)";

/** Sentiments in the popover, best first (the prototype's order). */
const SENTIMENT_ORDER: Array<SocialSentiment> = ["positive", "neutral", "mixed", "negative"];

/** The countable facets; `date` is the brush's token and has no popover. */
export type SocialFilterMenuFacet = Exclude<SocialTopicFacet, "date">;

/** The countable facets in pill order. */
export const SOCIAL_FILTER_MENU_FACETS: Array<SocialFilterMenuFacet> = [
  "channel",
  "brand",
  "sentiment",
  "source",
  "theme",
];

/** The filter state field each facet sets. */
export const SOCIAL_FACET_FILTER_KEY = {
  channel: "platform",
  brand: "brandId",
  sentiment: "sentiment",
  source: "source",
  theme: "themeId",
} as const satisfies Record<SocialFilterMenuFacet, keyof SocialTopicFilterState>;

/** One row of a filter menu. `value` null is "Any". */
export interface SocialFilterMenuOption {
  value: string | null;
  label: ReactNode;
  /** The count in the current measure; absent draws no count and no bar (the sort menu). */
  measure?: number;
  /** `measure` as shown (compact). */
  measureLabel?: string;
  isDisabled?: boolean;
  testId: string;
}

/** True at the bottom sheet breakpoint and below; false where `matchMedia` is missing. */
export function useIsFilterSheetViewport(): boolean {
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const query = window.matchMedia(SOCIAL_FILTER_SHEET_QUERY);
    setIsNarrow(query.matches);
    const onChange = () => setIsNarrow(query.matches);
    query.addEventListener?.("change", onChange);
    return () => query.removeEventListener?.("change", onChange);
  }, []);

  return isNarrow;
}

interface SocialSentimentDotProps {
  sentiment: SocialSentiment;
}

/** The sentiment dot: green and red carry meaning, the others stay neutral. */
export function SocialSentimentDot(props: SocialSentimentDotProps) {
  const { sentiment } = props;

  const tone =
    sentiment === "positive"
      ? "bg-chart-positive"
      : sentiment === "negative"
        ? "bg-chart-negative"
        : sentiment === "mixed"
          ? "bg-muted-foreground"
          : "bg-muted-foreground/50";

  return <span aria-hidden="true" className={cn("size-2 shrink-0 rounded-full", tone)} />;
}

interface SocialFacetValueLabelProps {
  facet: SocialFilterMenuFacet;
  value: string;
  context: SocialTopicSlotContext;
}

/**
 * A facet value as the pill token and the popover row show it: the
 * platform mark, the sentiment dot, or the brand or Theme name.
 */
export function SocialFacetValueLabel(props: SocialFacetValueLabelProps) {
  const { facet, value, context } = props;

  const { t } = useTranslation("app");

  if (facet === "channel") {
    const platform = value as SocialPlatform;
    return (
      <span className="inline-flex min-w-0 items-center gap-2">
        <SocialPlatformIcon platform={platform} className="size-4 shrink-0" />
        <span className="truncate">{SOCIAL_PLATFORM_LABELS[platform] ?? value}</span>
      </span>
    );
  }
  if (facet === "sentiment") {
    const sentiment = value as SocialSentiment;
    return (
      <span className="inline-flex min-w-0 items-center gap-2">
        <SocialSentimentDot sentiment={sentiment} />
        <span className="truncate">{t(`socialListening.sentiment.${sentiment}`)}</span>
      </span>
    );
  }
  if (facet === "source") {
    return <span className="truncate">{t(`socialListening.postSource.${value as SocialPostSource}`)}</span>;
  }
  if (facet === "brand") {
    return (
      <span className="inline-flex min-w-0 items-center gap-2">
        <SocialBrandMark
          logoPath={context.brandLogoPaths?.get(value)}
          swatch={null}
          testId={`social-filter-brand-mark-${value}`}
        />
        <span className="truncate">{context.brandNames.get(value) ?? value}</span>
      </span>
    );
  }
  const name =
    value === SOCIAL_OTHER_THEME_ID
      ? t("socialListening.topicView.other")
      : context.themeNames.get(value);
  return <span className="truncate">{name ?? value}</span>;
}

export interface SocialFilterMenuSearch {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
  emptyMessage?: string;
}

interface SocialFilterMenuProps {
  /** The listbox's accessible name. */
  label: string;
  options: Array<SocialFilterMenuOption>;
  selected: string | null;
  onSelect: (value: string | null) => void;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** The pill. */
  trigger: ReactNode;
  /** Under the rows, e.g. "Counts show reach". */
  footer?: ReactNode;
  /** Controlled search. The caller supplies the filtered options. */
  search?: SocialFilterMenuSearch;
  /** Skeleton rows until the counts arrive. */
  isLoading?: boolean;
  testId: string;
}

/**
 * A pill's menu: a popover under the pill, a bottom sheet at 760 and below.
 * Rows are options with an optional count and a 2 px primary bar; Up and
 * Down move, Enter selects, Escape closes.
 */
export function SocialFilterMenu(props: SocialFilterMenuProps) {
  const {
    label,
    options,
    selected,
    onSelect,
    isOpen,
    onOpenChange,
    trigger,
    footer,
    search,
    isLoading = false,
    testId,
  } = props;

  const listRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const isSheet = useIsFilterSheetViewport();

  const maxMeasure = Math.max(
    1,
    ...options.filter((option) => option.value != null).map((option) => option.measure ?? 0),
  );

  const focusInitial = () => {
    if (search && searchRef.current) {
      searchRef.current.focus();
      return;
    }
    const list = listRef.current;
    if (!list) {
      return;
    }
    const target =
      list.querySelector<HTMLButtonElement>('[role="option"][aria-selected="true"]:not(:disabled)') ??
      list.querySelector<HTMLButtonElement>('[role="option"]:not(:disabled)');
    target?.focus();
  };

  const onListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
      return;
    }
    event.preventDefault();
    const enabled = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]:not(:disabled)') ?? [],
    );
    if (enabled.length === 0) {
      return;
    }
    const index = enabled.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === "ArrowDown" ? 1 : -1;
    const next = index < 0 ? 0 : (index + step + enabled.length) % enabled.length;
    enabled[next].focus();
  };

  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      listRef.current?.querySelector<HTMLButtonElement>('[role="option"]:not(:disabled)')?.focus();
    }
  };

  const onEscape = (event: KeyboardEvent | globalThis.KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onOpenChange(false);
  };

  const onPick = (value: string | null) => {
    onSelect(value);
    onOpenChange(false);
  };

  const onAutoFocus = (event: Event) => {
    event.preventDefault();
    focusInitial();
  };

  const rows = isLoading
    ? [0, 1, 2].map((index) => <Skeleton key={index} className="mx-2.5 my-2 h-5" />)
    : options.map((option) => {
        const isSelected = option.value === selected;
        const hasMeasure = option.measure != null;
        const share = hasMeasure ? ((option.measure ?? 0) * 100) / maxMeasure : 0;
        return (
          <button
            key={option.value ?? "any"}
            type="button"
            role="option"
            aria-selected={isSelected}
            disabled={Boolean(option.isDisabled) && !isSelected}
            className={cn(
              "grid w-full grid-cols-[minmax(0,1fr)_auto_18px] items-center gap-x-2.5 rounded-md px-2.5 py-2 text-left text-sm",
              "outline-none transition-colors hover:bg-accent focus-visible:bg-accent",
              "disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent",
              isSelected && "bg-primary/10 hover:bg-primary/10",
            )}
            onClick={() => onPick(option.value)}
            data-testid={option.testId}
          >
            <span className="flex min-w-0 items-center gap-2">{option.label}</span>
            <span className="text-muted-foreground tabular-nums">
              {hasMeasure ? option.measureLabel : null}
            </span>
            <span className="flex justify-end text-primary">
              {isSelected ? <Check className="size-4" aria-hidden="true" /> : null}
            </span>
            {hasMeasure && option.value != null ? (
              <span
                aria-hidden="true"
                className="col-span-2 mt-1.5 h-0.5 overflow-hidden rounded-full bg-[hsl(var(--chart-bar-track))]"
              >
                <span
                  className="block h-full rounded-full bg-primary"
                  style={{ width: `${share}%` }}
                />
              </span>
            ) : null}
          </button>
        );
      });

  const list = (
    <div
      ref={listRef}
      role="listbox"
      aria-label={label}
      className={cn("flex flex-col", search && "max-h-64 overflow-y-auto")}
      onKeyDown={onListKeyDown}
      data-testid={`${testId}-list`}
    >
      {rows}
      {footer ? (
        <div className="px-2.5 pb-0.5 pt-1.5 text-xs text-muted-foreground">{footer}</div>
      ) : null}
    </div>
  );

  const menu = (
    <>
      {search ? (
        <div className="border-b border-border px-1 pb-1.5">
          <Input
            ref={searchRef}
            type="search"
            value={search.value}
            onChange={(event) => search.onChange(event.target.value)}
            onKeyDown={onSearchKeyDown}
            aria-label={search.label}
            placeholder={search.placeholder}
            autoComplete="off"
            className="h-9 border-0 bg-transparent shadow-none"
            data-testid={`${testId}-search`}
          />
        </div>
      ) : null}
      {list}
      {search?.emptyMessage && !isLoading && options.length === 0 ? (
        <div role="status" className="px-2.5 py-3 text-sm text-muted-foreground">{search.emptyMessage}</div>
      ) : null}
    </>
  );

  return (
    <Popover open={isOpen && !isSheet} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      {isSheet ? (
        <Sheet open={isOpen} onOpenChange={onOpenChange}>
          <SheetContent
            side="bottom"
            className="rounded-t-2xl p-2 pb-4"
            onOpenAutoFocus={onAutoFocus}
            onEscapeKeyDown={onEscape}
            data-testid={`${testId}-sheet`}
          >
            <SheetTitle className="px-2.5 pb-1 pt-2 text-sm font-medium">{label}</SheetTitle>
            {menu}
          </SheetContent>
        </Sheet>
      ) : (
        <PopoverContent
          align="start"
          className="w-[300px] max-w-[calc(100vw-16px)] rounded-xl p-1.5 shadow-lg"
          onOpenAutoFocus={onAutoFocus}
          onEscapeKeyDown={onEscape}
          data-testid={`${testId}-popover`}
        >
          {menu}
        </PopoverContent>
      )}
    </Popover>
  );
}

/** The platforms the Source facet reads: the selected channel, else every channel of the Topic. */
function sourceFacetPlatforms(context: SocialTopicSlotContext): Array<SocialPlatform> {
  const selected = context.topicFilters.filters.platform;
  return selected ? [selected] : [...new Set(context.universe.channels.map((channel) => channel.platform))];
}

/** The values of one facet: every one the Topic names; "Other" only when it holds posts or is set. */
function facetValues(
  facet: SocialFilterMenuFacet,
  context: SocialTopicSlotContext,
  counts: SocialTopicCountsResponse | null,
  selected: string | null,
): Array<string> {
  const { universe } = context;
  if (facet === "channel") {
    return [...new Set(universe.channels.map((channel) => channel.platform))];
  }
  if (facet === "brand") {
    return universe.brands.map((brand) => brand.id);
  }
  if (facet === "sentiment") {
    return SENTIMENT_ORDER;
  }
  if (facet === "source") {
    const { sources } = socialSourceFacet(sourceFacetPlatforms(context));
    return selected && !(sources as Array<string>).includes(selected) ? [...sources, selected] : sources;
  }
  const other = counts?.cells.find((cell) => cell.key === SOCIAL_OTHER_THEME_ID)?.count ?? 0;
  return [
    ...universe.topics.map((topic) => topic.id),
    ...(other > 0 || selected === SOCIAL_OTHER_THEME_ID ? [SOCIAL_OTHER_THEME_ID] : []),
  ];
}

interface SocialFilterPopoverProps {
  facet: SocialTopicFacet;
  context: SocialTopicSlotContext;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** The facet pill. */
  trigger: ReactNode;
}

/**
 * One facet's options in the current measure, zero rows disabled; a bottom
 * sheet at 760 and below. Its counts leave the facet's own filter out and
 * keep every other one (search, author and window included). They load
 * when the menu opens; the page's own facet counts share the cache key.
 */
export function SocialFilterPopover(props: SocialFilterPopoverProps) {
  const { facet, context, isOpen, onOpenChange, trigger } = props;

  const { t, i18n } = useTranslation("app");

  const menuFacet: SocialFilterMenuFacet | null = facet === "date" ? null : facet;
  const { topicFilters, universe, refreshKey } = context;
  const metric: SocialTopicMetric = topicFilters.filters.metric;

  const { counts } = useSocialUniverseCounts(
    menuFacet && isOpen ? universe.token : null,
    {
      groupBy: menuFacet ?? "channel",
      filters: topicFilters.facetFilters(menuFacet ?? undefined),
    },
    refreshKey,
  );

  if (!menuFacet) {
    return <>{trigger}</>;
  }

  const filterKey = SOCIAL_FACET_FILTER_KEY[menuFacet];
  const selected = (topicFilters.filters[filterKey] as string | null) ?? null;
  const testId = `topic-filter-${menuFacet}`;
  const format = (value: number) => formatSocialCount(value, i18n.language);
  const anyMeasure = counts ? measureOf(metric, counts.total, counts.views) : undefined;

  const options: Array<SocialFilterMenuOption> = [
    {
      value: null,
      label: <span>{t("socialListening.topicPage.filters.any")}</span>,
      measure: anyMeasure,
      measureLabel: anyMeasure != null ? format(anyMeasure) : undefined,
      testId: `${testId}-option-any`,
    },
    ...facetValues(menuFacet, context, counts, selected).map((value) => {
      const cell = counts?.cells.find((item) => item.key === value);
      const posts = cell?.count ?? 0;
      const measure = counts ? measureOf(metric, posts, cell?.views) : undefined;
      return {
        value,
        label: <SocialFacetValueLabel facet={menuFacet} value={value} context={context} />,
        measure,
        measureLabel: measure != null ? format(measure) : undefined,
        isDisabled: counts != null && posts === 0,
        testId: `${testId}-option-${value}`,
      };
    }),
  ];

  const onSelect = (value: string | null) => {
    // The facet's key and its value type go together (SOCIAL_FACET_FILTER_KEY).
    topicFilters.setFilter(filterKey, value as SocialTopicFilterState[typeof filterKey]);
  };

  return (
    <SocialFilterMenu
      label={t(`socialListening.topicPage.filters.${menuFacet}`)}
      options={options}
      selected={selected}
      onSelect={onSelect}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      trigger={trigger}
      footer={
        menuFacet === "source" ? (
          <SourceFacetFooter context={context} countsShow={t(`socialListening.topicPage.filters.countsShow.${metric}`)} />
        ) : (
          t(`socialListening.topicPage.filters.countsShow.${metric}`)
        )
      }
      isLoading={counts == null}
      testId={testId}
    />
  );
}

interface SourceFacetFooterProps {
  context: SocialTopicSlotContext;
  countsShow: string;
}

/** Under the Source rows: the measure, then each label not available on a channel in scope, and where. */
function SourceFacetFooter(props: SourceFacetFooterProps) {
  const { context, countsShow } = props;

  const { t, i18n } = useTranslation("app");

  const { unavailable } = socialSourceFacet(sourceFacetPlatforms(context));
  const list = new Intl.ListFormat(intlLocaleForUi(i18n.language), { style: "long", type: "conjunction" });

  return (
    <span className="flex flex-col gap-1">
      <span>{countsShow}</span>
      {unavailable.map((entry) => (
        <span key={entry.source} data-testid={`topic-filter-source-unavailable-${entry.source}`}>
          {t("socialListening.topicPage.filters.sourceUnavailable", {
            label: t(`socialListening.postSource.${entry.source}`),
            platforms: list.format(entry.platforms.map((platform) => SOCIAL_PLATFORM_LABELS[platform])),
          })}
        </span>
      ))}
    </span>
  );
}
